import type { DiscordSDK, DiscordSDKMock } from '@discord/embedded-app-sdk'
import { api, ApiError, setSessionToken, type DiscordSession, type DiscordSessionRequest } from '../api'
import { followLocale } from '../i18n'
import { setHardwareDecoding } from '../player/codecs'

const params = new URLSearchParams(location.search)

/**
 * Whether the app runs as a Discord Activity: Discord opens it with
 * frame_id and instance_id in the URL. Development builds can fake that
 * with ?discord_mock=<user>&instance_id=<id> for browser tests.
 */
export const inDiscord = params.has('frame_id') || (import.meta.env.DEV && params.has('discord_mock'))

type Sdk = DiscordSDK | DiscordSDKMock

let sdk: Sdk | null = null
let mocked = false
// The website, for links that leave Discord.
let site = ''

/** The address of the website, e.g. https://watch.example.com. */
export function siteUrl() {
  return site
}

/**
 * Why the activity could not start. not_activated: the first room in a
 * Discord server needs the room creation token (also invalid_token and
 * too_many, its answers).
 */
export type DiscordProblem = 'not_configured' | 'denied' | 'not_in_activity' | 'server' | 'not_activated' | 'invalid_token' | 'too_many'

export class DiscordStartError extends Error {
  constructor(readonly problem: DiscordProblem) {
    super(problem)
  }
}

// identify and guilds.members.read give names and avatars as people have
// them on the server; rpc.activities.write shows what is on in profiles.
const SCOPES = ['identify', 'guilds', 'guilds.members.read', 'rpc.activities.write'] as const

/**
 * Connects to Discord, signs the user in and joins the room of the
 * activity instance (see startDiscord). The server remembers the session; it is also kept for
 * requests in case the partitioned cookie is refused.
 */
let starting: Promise<DiscordSession> | null = null

/** Signs in once; after a failure the next call retries. */
export function startDiscord(): Promise<DiscordSession> {
  starting ??= connect().catch((e) => {
    starting = null
    throw e
  })
  return starting
}

async function connect(): Promise<DiscordSession> {
  const features = await api.features().catch(() => {
    throw new DiscordStartError('server')
  })
  const clientId = features.discord?.clientId
  if (!clientId) throw new DiscordStartError('not_configured')
  site = features.discord?.siteUrl ?? ''

  if (!sdk) {
    // Loaded only inside Discord: the website does not need it.
    const lib = await import('@discord/embedded-app-sdk')
    mocked = !params.has('frame_id')
    sdk = mocked ? mockSdk(lib.DiscordSDKMock, clientId) : new lib.DiscordSDK(clientId)
  }
  await sdk.ready()
  let code: string
  try {
    ;({ code } = await sdk.commands.authorize({
      client_id: clientId,
      response_type: 'code',
      state: '',
      prompt: 'none',
      scope: [...SCOPES],
    }))
  } catch {
    throw new DiscordStartError('denied')
  }
  try {
    const { accessToken } = await api.discordToken(code)
    await sdk.commands.authenticate({ access_token: accessToken })
    checkHardwareAcceleration(sdk)
    // The voice channel names the room; it is only a label.
    let channelName = ''
    if (sdk.channelId && sdk.guildId) {
      channelName = await sdk.commands
        .getChannel({ channel_id: sdk.channelId })
        .then((c) => c.name ?? '')
        .catch(() => '')
    }
    await sdk.commands
      .userSettingsGetLocale()
      .then(({ locale }) => locale && followLocale(locale))
      .catch(() => {})
    pending = { accessToken, instanceId: instanceId(sdk), guildId: sdk.guildId, channelId: sdk.channelId, channelName }
    return await openSession(pending)
  } catch (e) {
    throw problemOf(e)
  }
}

// The sign-in waiting for the room creation token, see activateDiscord.
let pending: DiscordSessionRequest | null = null

/** Activates the Discord server with the room creation token and signs in. */
export async function activateDiscord(createToken: string): Promise<DiscordSession> {
  if (!pending) throw new DiscordStartError('server')
  try {
    return await openSession({ ...pending, createToken })
  } catch (e) {
    throw problemOf(e)
  }
}

async function openSession(req: DiscordSessionRequest): Promise<DiscordSession> {
  const session = await api.discordSession(req)
  pending = null
  setSessionToken(session.sessionToken)
  return session
}

function problemOf(e: unknown): DiscordStartError {
  if (e instanceof DiscordStartError) return e
  if (e instanceof ApiError) {
    if (e.code === 'not_activated' || e.code === 'invalid_token' || e.code === 'too_many') return new DiscordStartError(e.code)
    if (e.status === 403) return new DiscordStartError('not_in_activity')
    if (e.status === 401) return new DiscordStartError('denied')
  }
  return new DiscordStartError('server')
}

/**
 * Without hardware acceleration Discord's Chromium cannot decode HEVC but
 * claims it can. Discord tells whether it is on and offers to turn it on;
 * until then the server's H.264 transcode is asked for. Not awaited: the
 * offer waits for the user.
 */
function checkHardwareAcceleration(s: Sdk) {
  void s.commands
    .encourageHardwareAcceleration()
    .then(({ enabled }) => setHardwareDecoding(enabled))
    .catch(() => {})
}

function instanceId(s: Sdk): string {
  return mocked ? (params.get('instance_id') ?? s.instanceId) : s.instanceId
}

/** A stand-in for Discord in development: ?discord_mock=a signs in as user "a". */
function mockSdk(Mock: typeof DiscordSDKMock, clientId: string): DiscordSDKMock {
  const user = params.get('discord_mock') || 'a'
  const mock = new Mock(clientId, params.get('guild_id') ?? 'mock-guild', params.get('channel_id') ?? 'mock-channel', null)
  mock._updateCommandMocks({
    authorize: async () => ({ code: `code-${user}` }),
    getChannel: async () => ({ id: 'mock-channel', type: 2, name: params.get('channel_name') ?? 'Кинозал', voice_states: [], messages: [] }) as never,
  })
  return mock
}

/** Opens Discord's dialog to invite people to the activity. */
export function invite() {
  void sdk?.commands.openInviteDialog().catch(() => {})
}

/** Opens a link in the browser: the activity iframe cannot navigate away. */
export function openExternal(url: string) {
  // The mock opens a tab, so tests can follow the link.
  if (mocked) window.open(url, '_blank')
  else void sdk?.commands.openExternalLink({ url }).catch(() => {})
}

const LAYOUT_MODE_UPDATE = 'ACTIVITY_LAYOUT_MODE_UPDATE' as const

/**
 * Calls fn with whether Discord shows the activity small (picture in
 * picture, or a tile among the call's videos).
 */
export function onCompactLayout(fn: (compact: boolean) => void): () => void {
  const s = sdk
  if (!s) return () => {}
  const handler = (e: { layout_mode: number }) => fn(e.layout_mode !== 0)
  void s.subscribe(LAYOUT_MODE_UPDATE, handler).catch(() => {})
  return () => void s.unsubscribe(LAYOUT_MODE_UPDATE, handler).catch(() => {})
}

/** What the room shows in people's Discord profiles. */
export interface Presence {
  title: string
  state: string
  /** Unix milliseconds when the current film started and ends, if playing. */
  start?: number
  end?: number
}

let lastPresence = ''

/** Shows the presence as "Watching …" in Discord; repeated values are skipped. */
export function setPresence(p: Presence | null) {
  const s = sdk
  if (!s) return
  const key = JSON.stringify(p)
  if (key === lastPresence) return
  lastPresence = key
  const activity = p
    ? {
        type: 3, // Watching
        details: p.title.slice(0, 128),
        state: p.state.slice(0, 128),
        ...(p.start && p.end ? { timestamps: { start: p.start, end: p.end } } : {}),
      }
    : {}
  void s.commands.setActivity({ activity }).catch(() => {})
}


